import { Action, createSlice } from '@reduxjs/toolkit';
import { jwtDecode } from 'jwt-decode';
import { API_SHORT } from '../constants';
import withLocalStorage, {
    LegacyAuthStore,
    LegacyUserToken,
} from '../util/withLocalStorage';
import { ROLES } from '../util/enums';
import { LoadUser, UnloadUser } from './userSlice';
import simpleBackendRequest from './network/simpleBackendRequest';
import { PbDispatch, PbGetState } from './index';
import useSessionStorage from '../util/useSessionStorage';
import {
    pbRequestFailure,
    pbRequestReset,
    pbRequestStart,
    pbRequestSuccess,
} from './pbRequestSlice';
import { PbRequest } from './pbRequest';
import { goToActivate } from '../nav/navigationService';

const {
    clear: clearLocalStorage,
    setRegisteredEmail,
    getAuthStore,
    setAuthStore,
} = withLocalStorage();
const { clear: clearSessionStorage } = useSessionStorage();

export const FULL_RESET = 'RESET/resetAll';
const ACTION_RESET_ALL: Action = { type: FULL_RESET };

interface LegacyAuthResponse {
    token?: string;
    message?: string;
    tags?: string[];
}

interface LoginRequest {
    email: string;
    password: string;
}

interface AuthStore {
    authentication: AuthenticationState;
}

enum AuthenticationErrors {
    UNKNOWN = 'UNKNOWN',
    INACTIVE = 'INACTIVE',
    WRONG_CREDENTIALS = 'WRONG_CREDENTIALS',
    USER_ACTIVATING = 'USER_ACTIVATING',
    USER_BLOCKED = 'USER_BLOCKED',
    USER = 'USER',
}

interface AuthenticationState {
    loggedIn: boolean;
    token?: string;
    error?: AuthenticationErrors;
    email?: string;
    password?: string;
}

export function getAuthPreload(): AuthStore {
    const storedAuth = getAuthStore();
    const preloaded: Partial<AuthStore> = {
        authentication: {
            loggedIn: storedAuth?.loggedIn || false,
            token: storedAuth?.token,
        },
    };

    return { ...initialState, ...preloaded };
}

const initialState: AuthStore = {
    authentication: {
        loggedIn: false,
        token: undefined,
        error: undefined,
    },
};

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        setAuthenticationState(state, action) {
            state.authentication = { ...action.payload };
        },
    },
});

const { setAuthenticationState } = authSlice.actions;

export function LoginWithStoredCredentials(
    callback?: (accountingAreaCount: number) => void
) {
    return async (dispatch: PbDispatch, getState: PbGetState) => {
        const email = getState().auth.authentication.email;
        const password = getState().auth.authentication.password;

        await LoginAsPartner(email, password, callback)(dispatch, getState);
    };
}

export function LoginAsPartner(
    email: string,
    password: string,
    callback?: (
        accountingAreaCount: number,
        canCreateAccountingAreas: boolean
    ) => void
) {
    return async (dispatch: PbDispatch, getState: PbGetState) => {
        try {
            const request: RequestInit = {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    username: email,
                    password: password,
                }),
            };
            dispatch(pbRequestStart(PbRequest.LOGIN));
            const response: Response = await simpleBackendRequest(
                `${API_SHORT}/auth`,
                request
            );

            const successResponse =
                response.status >= 200 && response.status < 300;
            const data: LegacyAuthResponse = await response.json();

            if (successResponse && data.token) {
                dispatch(pbRequestSuccess(PbRequest.LOGIN));
            } else {
                dispatch(pbRequestFailure(PbRequest.LOGIN, []));
            }

            if (successResponse && data.token) {
                const decoded = jwtDecode(data.token) as LegacyUserToken;
                if (checkAuthRole(decoded)) {
                    await handleLoginAsPartnerCompleted(data.token, decoded);
                } else {
                    await handleLoginFailed(
                        dispatch,
                        { email, password },
                        { tags: [AuthenticationErrors.WRONG_CREDENTIALS] }
                    );
                }
            } else {
                await handleLoginFailed(dispatch, { email, password }, data);
            }
        } catch (e) {
            console.log('login failed', e);
            dispatch(pbRequestFailure(PbRequest.LOGIN, []));
            await handleLoginFailed(dispatch, { email, password });
        }

        async function handleLoginAsPartnerCompleted(
            token: string,
            user: LegacyUserToken
        ) {
            await handleLoginCompleted(dispatch, getState, token, user);
            // AuthActions.loadStaticData(role)
            // --> this triggers UserActions.load(userRole)
            // --> UserStore.onLoad
            // -----> UserActions.loadUserData()
            // -----> PartnerManagerActions.loadPartnerManagerDetails()
            // -----> PartnerManagerActions.loadPartners()
            // -> implicitly, after loadUserData completed -> UserStore.userLoaded(userData)
            setRegisteredEmail(null);
            // PartnerManagerActions.loadPartners(REDIRECT_FROM_AUTH)

            const partners =
                getState().partnerManager.getPartnersResult?.partners ?? [];
            const canCreateAccountingAreas =
                getState().partnerManager.canCreateAccountingArea;

            const numberOfAccountingAreas = partners ? partners.length : 0;
            callback?.(numberOfAccountingAreas, canCreateAccountingAreas);
        }
    };
}

async function handleLoginCompleted(
    dispatch: PbDispatch,
    getState: PbGetState,
    token: string,
    user: LegacyUserToken
) {
    dispatch(setAuthenticationState({ token, loggedIn: true }));

    await LoadUser()(dispatch, getState);

    const legacyStorage: LegacyAuthStore = {
        loggedIn: true,
        token,
        user,
    };
    setAuthStore(legacyStorage);
}

async function handleLoginFailed(
    dispatch: PbDispatch,
    login: LoginRequest,
    data?: LegacyAuthResponse
) {
    const error = getErrorFromResponse(data);
    if (error == AuthenticationErrors.USER_ACTIVATING) {
        // set username/password from response ?!
        dispatch(
            setAuthenticationState({
                loggedIn: false,
                email: login.email,
                password: login.password,
            })
        );
        goToActivate();
    } else {
        dispatch(setAuthenticationState({ loggedIn: false, error }));
    }

    const legacyStorage: LegacyAuthStore = {
        loggedIn: false,
    };
    setAuthStore(legacyStorage);
}

function getErrorFromResponse(data?: LegacyAuthResponse) {
    const errorFromTags = maybeParseErrorTags(data);
    if (errorFromTags) {
        return errorFromTags;
    }
    if (data) {
        return parseErrorMessage(data.message);
    }
    return AuthenticationErrors.UNKNOWN;
}

function maybeParseErrorTags(
    data?: LegacyAuthResponse
): AuthenticationErrors | undefined {
    if (!data?.tags) {
        return undefined;
    }

    if (data.tags.includes(AuthenticationErrors.WRONG_CREDENTIALS)) {
        return AuthenticationErrors.WRONG_CREDENTIALS;
    } else if (data.tags.includes(AuthenticationErrors.USER_ACTIVATING)) {
        return AuthenticationErrors.USER_ACTIVATING;
    } else if (data.tags.includes(AuthenticationErrors.USER_BLOCKED)) {
        return AuthenticationErrors.USER_BLOCKED;
    } else if (data.tags.includes(AuthenticationErrors.INACTIVE)) {
        return AuthenticationErrors.INACTIVE;
    } else {
        return AuthenticationErrors.UNKNOWN;
    }
}

function parseErrorMessage(message: string): AuthenticationErrors {
    if (/inactive\/disabled/.test(message)) {
        return AuthenticationErrors.INACTIVE;
    }
    if (/Bad username or password/.test(message)) {
        return AuthenticationErrors.WRONG_CREDENTIALS;
    }
    if (/could not be found/.test(message)) {
        return AuthenticationErrors.WRONG_CREDENTIALS;
    }
    return AuthenticationErrors.UNKNOWN;
}

export function RefreshToken() {
    return async (dispatch: PbDispatch, getState: PbGetState) => {
        try {
            const oldToken = getState().auth.authentication.token;
            const request: RequestInit = {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${oldToken}`,
                },
            };
            dispatch(pbRequestStart(PbRequest.LOGIN));
            const resp: Response = await simpleBackendRequest(
                `${API_SHORT}/auth/refresh`,
                request
            );
            const successfulRequest = resp.status >= 200 && resp.status < 300;
            let success = false;
            if (successfulRequest) {
                const data = (await resp.json()) as LegacyAuthResponse;
                dispatch(pbRequestSuccess(PbRequest.LOGIN));
                if (data.token) {
                    const decoded = jwtDecode(data.token) as LegacyUserToken;
                    if (checkAuthRole(decoded)) {
                        await handleLoginCompleted(
                            dispatch,
                            getState,
                            data.token,
                            decoded
                        );
                        success = true;
                    }
                }
            }
            if (!success) {
                dispatch(pbRequestReset(PbRequest.LOGIN));
            }
        } catch {
            dispatch(pbRequestReset(PbRequest.LOGIN));
        }
    };
}

function checkAuthRole(user: LegacyUserToken) {
    return user.role.includes(ROLES.partner_manager);
}

export function MaybeLogoutFromPartnerBoard() {
    return async (dispatch: PbDispatch, getState: PbGetState) => {
        if (getState().auth.authentication.loggedIn) {
            await LogoutFromPartnerBoard()(dispatch, getState);
        }
    };
}

export function LogoutFromPartnerBoard() {
    return async (dispatch: PbDispatch, getState: PbGetState) => {
        try {
            const loggedOutState: LegacyAuthStore = {
                loggedIn: false,
            };
            setAuthStore(loggedOutState);

            dispatch(setAuthenticationState(loggedOutState));
            await UnloadUser()(dispatch, getState);

            dispatch(FullResetAction());
            console.log('logout => reloading page');
            location.reload();
        } catch (e) {
            console.log('issue during logout', e);
        }
    };
}

export function FullResetAction() {
    return async (dispatch) => {
        clearLocalStorage();
        clearSessionStorage();
        dispatch(ACTION_RESET_ALL);
    };
}

export default authSlice.reducer;
